import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import {
  CheckIcon,
  ShieldCheckIcon,
  UserIcon,
  HeartIcon,
  LockClosedIcon,
  ClockIcon,
  SparklesIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  StarIcon,
  CalendarDaysIcon,
  EnvelopeIcon,
  CloudIcon,
  PlayCircleIcon,
  BriefcaseIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  WrenchScrewdriverIcon,
  BellAlertIcon,
  ArrowTrendingUpIcon,
  BookOpenIcon,
  ChevronDownIcon,
  CameraIcon,
} from '@heroicons/react/24/outline';

function openEarlyInterest() {
  window.dispatchEvent(new CustomEvent('open-early-interest'));
}

// ─── Hero ──────────────────────────────────────────────────────────────────────

function HeroFlowCard() {
  const sections = [
    {
      title: 'Coaching Insights',
      desc: 'Each session builds a clearer picture of your client\'s Executive Function needs, strengths, and patterns',
    },
    {
      title: 'Coaching Notes',
      desc: 'Structured session insights ready for your review',
    },
    {
      title: 'Session Prep',
      desc: 'Ready before every session starts. Walk in clear, confident, and prepared.',
    },
    {
      title: 'Action Items & Nudges',
      desc: 'Tasks assigned with due dates and priority, plus customizable reminders scheduled and sent automatically',
    },
    {
      title: 'Grow as a Coach',
      desc: 'Patterns in your coaching approach surface over time so you can see your strengths and keep growing',
    },
  ];

  return (
    <div className="bg-dark-card rounded-2xl shadow-lg border border-dark-border p-6 w-full max-w-sm mx-auto">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 bg-primary-600 rounded-xl flex items-center justify-center flex-shrink-0">
          <SparklesIcon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-sm font-bold text-white">Session Intelligence</p>
          <p className="text-xs text-gray-500">Every session documented, reviewed and customized</p>
        </div>
      </div>
      <div className="space-y-2.5">
        {sections.map((s, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <div className="w-4 h-4 bg-primary-900 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <CheckIcon className="w-2.5 h-2.5 text-primary-400" />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-100">{s.title}</p>
              <p className="text-xs text-gray-500 leading-snug">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5 bg-primary-900 border border-primary-800 rounded-xl px-4 py-3">
        <p className="text-xs text-primary-300 font-medium">Review, edit, and finalize everything before it reaches your client.</p>
      </div>
      <div className="mt-3 bg-primary-700 rounded-xl px-4 py-4">
        <p className="text-sm font-bold text-white mb-2">Fully Customizable</p>
        <ul className="space-y-1.5">
          <li className="text-xs text-primary-100 flex items-start gap-2"><span className="mt-0.5">•</span>Add, edit, or remove note sections</li>
          <li className="text-xs text-primary-100 flex items-start gap-2"><span className="mt-0.5">•</span>Define what goes inside each section</li>
          <li className="text-xs text-primary-100 flex items-start gap-2"><span className="mt-0.5">•</span>You control what clients see</li>
          <li className="text-xs text-primary-100 flex items-start gap-2"><span className="mt-0.5">•</span>Customize notes per client</li>
        </ul>
      </div>
    </div>
  );
}

function Hero() {
  const highlights = [
    {
      icon: <UsersIcon className="w-6 h-6 text-primary-400" />,
      text: 'Designed specifically for ADHD & Executive Function coaching',
    },
    {
      icon: <SparklesIcon className="w-6 h-6 text-primary-400" />,
      text: 'AI that understands coaching, not just transcription',
    },
    {
      icon: <ClockIcon className="w-6 h-6 text-primary-400" />,
      text: 'Spend less time documenting and more time coaching',
    },
  ];

  return (
    <section className="bg-dark-canvas py-12 pt-[40px]">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-start">
        {/* Left */}
        <div className="text-center lg:text-left">
          <h1 className="text-3xl font-extrabold text-white leading-tight mb-3 mt-10">
            ADHD coaching finally has its own platform.
          </h1>
          <p className="text-gray-400 mb-5 leading-relaxed">
            <img src="/coachsight-wordmark-transparent.png" alt="CoachSight" style={{ height: '1.4em', width: 'auto', display: 'inline', verticalAlign: 'middle', marginRight: '2px', marginBottom: '2px' }} /> produces pre-session preparation notes, ADHD aware session notes, actions, nudges and more for every session. Scheduling, payments, agreements, and all your admin tools included. No more juggling multiple apps.
          </p>
          <p className="text-xs font-semibold text-primary-300 mb-3">Free during beta · No credit card required during beta · 30% Founding Coach price locked in for life</p>
          <div className="flex flex-wrap gap-3 mb-6 justify-center lg:justify-start">
            <button
              onClick={openEarlyInterest}
              className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              Request Early Access
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {highlights.map((h, i) => (
              <div key={i} className="flex flex-col items-center text-center gap-2">
                <div className="w-12 h-12 bg-primary-900 rounded-xl flex items-center justify-center">
                  {h.icon}
                </div>
                <p className="text-xs text-gray-400 leading-snug">{h.text}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-6 justify-center lg:justify-start">
            <Link to="/privacy" onClick={() => window.scrollTo(0, 0)} className="text-xs text-gray-500 hover:text-primary-400 transition-colors">Privacy Policy</Link>
            <span className="text-gray-700 text-xs">·</span>
            <Link to="/terms" onClick={() => window.scrollTo(0, 0)} className="text-xs text-gray-500 hover:text-primary-400 transition-colors">Terms of Use</Link>
            <span className="text-gray-700 text-xs">·</span>
            <Link to="/ai-usage-data" onClick={() => window.scrollTo(0, 0)} className="text-xs text-gray-500 hover:text-primary-400 transition-colors">AI & Data Policy</Link>
          </div>
        </div>
        {/* Right */}
        <div className="mt-6 lg:mt-10">
          <p className="block lg:hidden text-xs font-semibold uppercase tracking-widest text-primary-400 mb-3 text-center">See it in action</p>
          <HeroFlowCard />
        </div>
      </div>
    </section>
  );
}

// ─── AI Trust ─────────────────────────────────────────────────────────────────

function AiTrust() {
  const principles = [
    {
      icon: <UserIcon className="w-5 h-5 text-primary-400" />,
      title: 'Human-First',
      bullets: [
        'You coach. CoachSight assists.',
        'You review, edit, and decide what gets sent.',
        'Your expertise and judgment are always in control.',
      ],
    },
    {
      icon: <CheckCircleIcon className="w-5 h-5 text-primary-400" />,
      title: 'Consent-First',
      bullets: [
        'Clients opt in before AI touches their session.',
        'Clients can opt out any time.',
        'No AI processing without explicit agreement.',
      ],
    },
    {
      icon: <ShieldCheckIcon className="w-5 h-5 text-primary-400" />,
      title: 'Privacy-First',
      bullets: [
        'Session data is never used to train AI models.',
        'Client names replaced before AI processing.',
        'Session data deleted from our AI provider after 30 days*.',
      ],
    },
    {
      icon: <LockClosedIcon className="w-5 h-5 text-primary-400" />,
      title: 'Ownership-First',
      bullets: [
        'Your data and IP are always yours.',
        'Never sold or shared with third parties.',
        'Export all your data whenever you want.',
        'Delete your profile and all data permanently.',
      ],
    },
    {
      icon: <HeartIcon className="w-5 h-5 text-primary-400" />,
      title: 'Coaches in Control',
      bullets: [
        'Edit or delete any AI produced session data or notes.',
        'Coach decides what their clients can see.',
        'Nothing is published to clients without your approval.',
      ],
    },
    {
      icon: <WrenchScrewdriverIcon className="w-5 h-5 text-primary-400" />,
      title: 'Coach-Customized',
      bullets: [
        'Use your own language, documents, and coaching framework.',
        'Add, edit, or remove note sections.',
        'Define and edit what goes inside each section.',
        'Customize notes per client.',
      ],
    },
  ];

  return (
    <section id="ai-trust" className="bg-dark-sidebar py-12 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-6">
          <h2 className="text-3xl font-extrabold text-white mb-3">AI You Can Trust</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">If you can't trust your AI, your clients can't trust you. Trust is the foundation of every coaching relationship, and shapes every decision we make about how CoachSight is built and how it handles your data.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
          {principles.map((item, i) => (
            <div key={i} className="bg-dark-card rounded-2xl p-4 border border-dark-border">
              <div className="w-9 h-9 bg-primary-900 rounded-xl flex items-center justify-center mb-3">
                {item.icon}
              </div>
              <p className="text-sm font-bold text-white mb-2">{item.title}</p>
              <ul className="space-y-1.5">
                {item.bullets.map((b, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-gray-400 leading-snug">
                    <span className="mt-0.5 w-4 h-4 flex-shrink-0 text-primary-400">✓</span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mb-5">* We are actively exploring zero-day retention with our AI provider, which would mean session recordings are never stored at all.</p>
        <div className="bg-primary-600 rounded-2xl p-3 inline-flex flex-row items-center gap-4">
          <p className="text-white font-semibold text-sm">Read how CoachSight uses technology, which providers we work with, and how your data is handled.</p>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link to="/ai-usage-data" onClick={() => window.scrollTo(0, 0)} className="bg-white text-primary-700 font-bold text-sm px-4 py-2 rounded-xl hover:bg-primary-50 transition-colors whitespace-nowrap">
              AI & Data Policy
            </Link>
            <Link to="/privacy" onClick={() => window.scrollTo(0, 0)} className="bg-primary-500 hover:bg-primary-400 text-white font-semibold text-sm px-4 py-2 rounded-xl transition-colors whitespace-nowrap">
              Privacy Policy
            </Link>
            <Link to="/terms" onClick={() => window.scrollTo(0, 0)} className="bg-primary-500 hover:bg-primary-400 text-white font-semibold text-sm px-4 py-2 rounded-xl transition-colors whitespace-nowrap">
              Terms of Use
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── ROI ──────────────────────────────────────────────────────────────────────

function Roi() {
  const [sessions, setSessions] = useState(20);

  const minPerSessionWithout = 20;
  const minPerSessionWith = 3;
  const hoursWithout = Math.round((sessions * minPerSessionWithout) / 60 * 10) / 10;
  const hoursWith = Math.round((sessions * minPerSessionWith) / 60 * 10) / 10;
  const hoursSaved = Math.round((hoursWithout - hoursWith) * 10) / 10;

  return (
    <div className="bg-dark-card rounded-2xl p-8 border border-dark-border shadow-sm">
      <h2 className="text-xl font-bold text-white mb-1">Your ROI at any volume</h2>
      <p className="text-sm text-gray-400 mb-6">Drag to your session count and see what CoachSight gives back.</p>

      {/* Slider */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-500">Sessions per month</span>
          <span className="text-sm font-bold text-primary-400">{sessions === 60 ? '60+' : sessions}</span>
        </div>
        <input
          type="range"
          min={5}
          max={60}
          step={5}
          value={sessions}
          onChange={e => setSessions(Number(e.target.value))}
          className="w-full h-2 bg-dark-hover rounded-full appearance-none cursor-pointer accent-primary-500"
        />
        {/* Labels at their true % positions on the 5–60 track */}
        <div className="relative h-4 mt-1.5">
          {([{ v: 15, pct: 18.2 }, { v: 30, pct: 45.5 }, { v: 45, pct: 72.7 }] as { v: number; pct: number }[]).map(({ v, pct }) => (
            <span key={v} className="absolute text-xs text-gray-500 -translate-x-1/2" style={{ left: `${pct}%` }}>{v}</span>
          ))}
          <span className="absolute text-xs text-gray-500 right-0">60+</span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <div className="bg-dark-hover rounded-xl p-4 border border-dark-border">
          <p className="text-xs text-gray-500 mb-1">Without CoachSight</p>
          <p className="text-xs text-gray-500 mb-2">~20 min / session</p>
          <p className="text-2xl font-extrabold text-gray-200">{hoursWithout}</p>
          <p className="text-xs font-semibold text-gray-400">hrs / month</p>
        </div>
        <div className="bg-green-500 rounded-xl p-4 text-center text-white flex flex-col items-center justify-center">
          <p className="text-xs font-semibold mb-1">Time saved</p>
          <p className="text-2xl font-extrabold">{hoursSaved}</p>
          <p className="text-xs font-semibold">hrs / month</p>
        </div>
        <div className="bg-primary-900 rounded-xl p-4 border border-primary-800">
          <p className="text-xs text-primary-400 mb-1">With CoachSight</p>
          <p className="text-xs text-gray-500 mb-2">~3 min review</p>
          <p className="text-2xl font-extrabold text-primary-300">{hoursWith}</p>
          <p className="text-xs font-semibold text-primary-400">hrs / month</p>
        </div>
      </div>
      <p className="text-xs text-gray-500 text-center mb-7">
        Based on ~20 min of post-session admin without CoachSight vs. ~3 min review with it. Estimates vary.
      </p>

    </div>
  );
}

// ─── Why CoachSight ───────────────────────────────────────────────────────────

function WhyCoachSight() {
  const reasons = [
    {
      icon: <BriefcaseIcon className="w-5 h-5 text-primary-400" />,
      title: 'Built from the ground up for ADHD and Executive Function coaches.',
      desc: 'Every tool coaches use was designed for therapists or generic practices. CoachSight was purpose-built for ADHD and Executive Function coaches.',
    },
    {
      icon: <SparklesIcon className="w-5 h-5 text-primary-400" />,
      title: 'One place. Not five.',
      desc: 'Notes, action items, nudges, scheduling, documents, cashflow, all connected. No more cobbling together tools that never talk to each other.',
    },
    {
      icon: <ClockIcon className="w-5 h-5 text-primary-400" />,
      title: 'Cut your post-session admin. Spend that time coaching.',
      desc: 'Reduce post-session admin by up to 85%. Review, edit, and finalize your notes in minutes, whatever your caseload.',
    },
    {
      icon: <ChartBarIcon className="w-5 h-5 text-primary-400" />,
      title: 'Patterns surfaced. Progress visible.',
      desc: 'Eight Executive Function domains tracked across every session. See where clients grow, where they struggle, and what your coaching is actually changing.',
    },
  ];

  const alwaysOn = [
    {
      icon: <WrenchScrewdriverIcon className="w-5 h-5 text-primary-400" />,
      title: 'All your needs in one tool',
      desc: 'Notes, scheduling, documents, invoicing, and client communication, consolidated. The cost savings alone often cover the subscription.',
    },
    {
      icon: <BellAlertIcon className="w-5 h-5 text-primary-400" />,
      title: 'No follow-up falls through the cracks',
      desc: 'Action items and nudges are created automatically every session. Clients stay accountable between sessions without extra effort from you.',
    },
    {
      icon: <ArrowTrendingUpIcon className="w-5 h-5 text-primary-400" />,
      title: 'Better outcomes, happier clients',
      desc: 'Structured notes and Executive Function pattern tracking make progress visible. Clients who see results are happier, more engaged, and more likely to refer.',
    },
    {
      icon: <BookOpenIcon className="w-5 h-5 text-primary-400" />,
      title: 'Always prepared, every session',
      desc: 'Pre-session prep is generated automatically before each call. Walk in knowing where you left off, what the client is working through, and what to focus on.',
    },
  ];

  return (
    <section id="why" className="bg-dark-canvas py-6 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-4 text-center lg:text-left">
          <h2 className="text-4xl font-extrabold text-white mb-1">Why CoachSight</h2>
          <p className="text-gray-400">Your expertise is your competitive advantage. CoachSight helps you spend more time using it.</p>
        </div>
        <div className="grid lg:grid-cols-2 gap-6 items-start">
          {/* Left, reasons + outcomes */}
          <div className="space-y-2">
            {reasons.map((r, i) => (
              <div key={i} className="flex gap-3 bg-dark-card rounded-2xl p-3 border border-dark-border">
                <div className="w-8 h-8 bg-primary-900 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                  {r.icon}
                </div>
                <div>
                  <p className="text-sm font-bold text-white mb-0.5">{r.title}</p>
                  <p className="text-xs text-gray-400 leading-snug">{r.desc}</p>
                </div>
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3 pt-1">
              {[
                { icon: <UsersIcon className="w-4 h-4 text-primary-400" />, label: 'Coach more effectively' },
                { icon: <BuildingOfficeIcon className="w-4 h-4 text-primary-400" />, label: 'Build your practice' },
                { icon: <StarIcon className="w-4 h-4 text-primary-400" />, label: 'Deliver better outcomes' },
                { icon: <HeartIcon className="w-4 h-4 text-primary-400" />, label: 'Take hours back' },
              ].map((o, i) => (
                <div key={i} className="flex items-center gap-2 bg-primary-900 rounded-xl px-3 py-2.5 border border-primary-800">
                  <div className="w-7 h-7 bg-dark-card rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                    {o.icon}
                  </div>
                  <p className="text-xs font-semibold text-gray-200">{o.label}</p>
                </div>
              ))}
            </div>
          </div>
          {/* Right, ROI */}
          <Roi />
        </div>
        <div className="mt-4 border-t border-dark-border pt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Constant ROI, regardless of session count</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {alwaysOn.map((item, i) => (
              <div key={i} className="flex gap-3">
                <div className="w-8 h-8 bg-primary-900 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  {item.icon}
                </div>
                <div>
                  <p className="text-xs font-bold text-white mb-0.5">{item.title}</p>
                  <p className="text-xs text-gray-400 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Three-column section ─────────────────────────────────────────────────────

function SplitPreviewRow({ icon, links, suffix }: {
  icon: React.ReactNode;
  links: { label: string; preview: { src: string; label: string } }[];
  suffix?: string;
}) {
  const [active, setActive] = useState<number | null>(null);
  return (
    <div className="flex items-center gap-3">
      <div className="w-7 h-7 bg-primary-900 rounded-lg flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <p className="text-sm text-gray-200">
        {links.map((link, i) => (
          <React.Fragment key={link.label}>
            {i > 0 && ' & '}
            <span
              className="relative inline-block"
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive(null)}
            >
              <span className="underline decoration-dotted underline-offset-2 cursor-default">{link.label}</span>
              {active === i && (
                <div className="absolute left-full pl-4 top-1/2 -translate-y-1/2 z-50">
                  <div className="bg-dark-card rounded-xl shadow-2xl border border-dark-border p-4 w-[520px]">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{link.preview.label}</p>
                    <div className="rounded-lg border border-dark-border overflow-hidden">
                      <img src={link.preview.src} alt={link.preview.label} className="w-full" />
                    </div>
                  </div>
                </div>
              )}
            </span>
          </React.Fragment>
        ))}
        {suffix && ` ${suffix}`}
      </p>
    </div>
  );
}

function FeaturePreviewPopup({ preview, wide, extraWide }: { preview: { src: string; label: string }[]; wide?: boolean; extraWide?: boolean }) {
  const widthClass = extraWide ? 'w-[900px]' : wide ? 'w-[700px]' : 'w-[520px]';
  return (
    <div className="absolute left-full pl-4 top-1/2 -translate-y-1/2 z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none group-hover:pointer-events-auto">
      <div className={`bg-dark-card rounded-xl shadow-2xl border border-dark-border p-4 ${widthClass}`}>
        <div className="overflow-y-auto max-h-[520px] space-y-4">
          {preview.map((s) => (
            <div key={s.src}>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{s.label}</p>
              <div className="rounded-lg border border-dark-border overflow-hidden">
                <img src={s.src} alt={s.label} className="w-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EverythingYouNeed() {
  const features: { icon: React.ReactNode; title: string; preview?: { src: string; label: string }[]; wide?: boolean; extraWide?: boolean }[] = [
    {
      icon: <ChartBarIcon className="w-5 h-5 text-primary-400" />,
      title: 'Executive Function theme extraction',
      preview: [{ src: '/screenshots/ef_themes.png', label: 'Executive Function Themes' }],
    },
    {
      icon: <SparklesIcon className="w-5 h-5 text-primary-400" />,
      title: 'ADHD/Executive Function aware notes',
      preview: [
        { src: '/screenshots/notes_overview.png', label: 'Session Overview' },
        { src: '/screenshots/adhd_notes.png', label: 'Client Insights' },
      ],
    },
    {
      icon: <SparklesIcon className="w-5 h-5 text-primary-400" />,
      title: 'Coach Notes',
      preview: [{ src: '/screenshots/coach_notes.png', label: 'Coach Notes' }],
    },
    {
      icon: <BriefcaseIcon className="w-5 h-5 text-primary-400" />,
      title: 'Action items with due dates & priority',
      preview: [{ src: '/screenshots/action_items.png', label: 'Action Items' }],
    },
    {
      icon: <EnvelopeIcon className="w-5 h-5 text-primary-400" />,
      title: 'Nudge scheduling & email delivery',
      preview: [{ src: '/screenshots/nudges.png', label: 'Nudges' }],
    },
    {
      icon: <ArrowTrendingUpIcon className="w-5 h-5 text-primary-400" />,
      title: 'Progress tracking',
      preview: [{ src: '/screenshots/progress_tracking.png', label: 'Progress Tracking' }],
      wide: true,
    },
    {
      icon: <ClockIcon className="w-5 h-5 text-primary-400" />,
      title: 'Pre-session Prep',
      preview: [{ src: '/screenshots/session_prep.png', label: 'Session Prep' }],
    },
    {
      icon: <DocumentTextIcon className="w-5 h-5 text-primary-400" />,
      title: 'Store and manage all practice documents',
      preview: [{ src: '/screenshots/doc_management.png', label: 'Document Management' }],
      wide: true,
    },
    {
      icon: <ChartBarIcon className="w-5 h-5 text-primary-400" />,
      title: 'Cashflow reporting',
      preview: [{ src: '/screenshots/cashflow.png', label: 'Cashflow' }],
      wide: true,
    },
    {
      icon: <UserIcon className="w-5 h-5 text-primary-400" />,
      title: 'Client self-scheduling',
      preview: [{ src: '/screenshots/client_selfsched.png', label: 'Client Self-Scheduling' }],
      wide: true,
    },
    {
      icon: <BriefcaseIcon className="w-5 h-5 text-primary-400" />,
      title: 'Full configurability of notes',
      preview: [{ src: '/screenshots/notes_config.png', label: 'Notes Configuration' }],
      extraWide: true,
    },
    {
      icon: <ClipboardDocumentListIcon className="w-5 h-5 text-primary-400" />,
      title: 'Custom forms & questionnaires',
      preview: [{ src: '/screenshots/forms.png', label: 'Forms Library' }],
      wide: true,
    },
    {
      icon: <CurrencyDollarIcon className="w-5 h-5 text-primary-400" />,
      title: 'Coaching packages & billing',
      preview: [{ src: '/screenshots/packages.png', label: 'Coaching Packages' }],
      wide: true,
    },
    {
      icon: <EnvelopeIcon className="w-5 h-5 text-primary-400" />,
      title: 'SMS delivery',
    },
  ];

  return (
    <div>
      <h2 className="text-xl font-bold text-white mb-1">Everything You Need</h2>
      <p className="text-sm text-gray-400 mb-1">All the essentials. In one place.</p>
      <p className="text-xs text-primary-400 mb-6 flex items-center gap-1">
        <CameraIcon className="w-3.5 h-3.5" />
        Hover any feature with a camera icon to preview a screenshot
      </p>
      <div className="space-y-3">
        <SplitPreviewRow
          icon={<UsersIcon className="w-5 h-5 text-primary-400" />}
          links={[
            { label: 'Coach', preview: { src: '/screenshots/coach_registration.png', label: 'Coach Registration' } },
            { label: 'Client', preview: { src: '/screenshots/client_registration.png', label: 'Client Registration' } },
          ]}
          suffix="Registration"
        />
        {features.map((f, i) => (
          <div key={i} className="relative group flex items-center gap-3">
            <div className="w-7 h-7 bg-primary-900 rounded-lg flex items-center justify-center flex-shrink-0">
              {f.icon}
            </div>
            <p className={`text-sm text-gray-200 ${f.preview ? 'cursor-default' : ''}`}>
              {f.title}
            </p>
            {f.preview && (
              <CameraIcon className="w-3.5 h-3.5 text-primary-400 flex-shrink-0" title="Hover to preview" />
            )}
            {f.preview && (
              <FeaturePreviewPopup preview={f.preview} wide={f.wide} extraWide={f.extraWide} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function WorksWith() {
  const integrations = [
    {
      icon: <PlayCircleIcon className="w-5 h-5 text-primary-400" />,
      label: 'Google Meet',
    },
    {
      icon: <PlayCircleIcon className="w-5 h-5 text-primary-500" />,
      label: 'Zoom',
    },
    {
      icon: <CalendarDaysIcon className="w-5 h-5 text-primary-400" />,
      label: 'Google Calendar',
    },
    {
      icon: <CurrencyDollarIcon className="w-5 h-5 text-primary-400" />,
      label: 'Stripe',
    },
    {
      icon: <CloudIcon className="w-5 h-5 text-yellow-400" />,
      label: 'Document Storage (Cloudflare R2)',
    },
    {
      icon: <SparklesIcon className="w-5 h-5 text-primary-400" />,
      label: 'AI Transcriber & Note Generator (Claude by Anthropic)',
    },
  ];

  return (
    <div>
      <h2 className="text-xl font-bold text-white mb-1">Works With Tools<br />You Already Use</h2>
      <div className="mt-6 space-y-4">
        {integrations.map((item, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
              {item.icon}
            </div>
            <span className="text-sm text-gray-300">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function WhatsComingBadge() {
  return (
    <span className="text-xs font-semibold text-primary-400 bg-primary-900 border border-primary-800 px-2 py-0.5 rounded-full whitespace-nowrap">
      Coming Soon
    </span>
  );
}

function WhatsComing() {
  const items: { label: string; done?: boolean }[] = [
    { label: 'Custom forms & questionnaires', done: true },
    { label: 'Coaching packages & billing', done: true },
    { label: 'SMS delivery', done: true },
    { label: 'Parent/Student Accounts' },
    { label: 'Coach growth insights' },
    { label: 'Incorporate VIA Strengths' },
    { label: 'Mentor Coaching and Coaching Session evaluation' },
    { label: 'Assessments' },
    { label: 'GDPR compliance' },
    { label: 'In-app messaging' },
    { label: 'Other calendar integrations' },
  ];

  return (
    <div>
      <h2 className="text-xl font-bold text-white mb-1">What's Coming</h2>
      <p className="text-sm text-gray-400 mb-6">On our roadmap.</p>
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={i} className="flex items-center justify-between gap-4">
            <span className="text-sm text-gray-300">{item.label}</span>
            {item.done ? (
              <span className="text-xs font-semibold text-green-400 bg-green-900/40 border border-green-700 px-2 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                Done
              </span>
            ) : (
              <WhatsComingBadge />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ThreeColSection() {
  return (
    <section id="features" className="bg-dark-sidebar py-12 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-12 divide-y md:divide-y-0 md:divide-x divide-dark-border">
        <EverythingYouNeed />
        <div className="pt-8 md:pt-0 md:pl-12" id="integrations">
          <WorksWith />
        </div>
        <div className="pt-8 md:pt-0 md:pl-12">
          <WhatsComing />
        </div>
      </div>
    </section>
  );
}

// ─── About ────────────────────────────────────────────────────────────────────

function About() {
  return (
    <section id="about" className="bg-dark-canvas py-12 scroll-mt-16 min-h-screen flex flex-col justify-center">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center">
        {/* Left, story */}
        <div className="text-center lg:text-left">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary-400 mb-3">Our Story</p>
          <h2 className="text-3xl font-extrabold text-white mb-4 leading-tight">
            Built by a coach,<br />for coaches.
          </h2>
          <div className="space-y-4 text-gray-300 leading-relaxed">
            <p>
              Hi, I am Pushan.<br />
              I'm one of those people who somehow convinced companies to let me build software and lead engineering teams for over 20 years. Fortunately, they never figured out I was mostly just making things up as I went.
            </p>
            <p>
              I've always loved building technology. But I also loved mentoring engineers, coaching new leaders, helping people get unstuck, and watching them grow into their roles and beyond. That eventually led me to Executive Function and ADHD coaching.
            </p>
            <p>
              As I began building my own coaching practice, I went looking for the platform I wanted to use. I found plenty of good coaching platforms that handled scheduling, forms, payments, and other administrative tasks. But I couldn't find one built specifically for ADHD and Executive Function coaches. AI note takers made documenting sessions easier, but many still produced notes that felt more like a quarterly business review than an ADHD coaching session. They captured what was said, but not what it meant for the client's ongoing coaching journey.
            </p>
            <p>
              Nothing helped me see beyond a single session by connecting patterns over time, building the client's story, or track progress through an ADHD/Executive Function lens.
            </p>
            <p className="font-semibold text-gray-100">
              I couldn't stop thinking about it. As an engineer, I have a hard time ignoring problems that feel solvable. So as any engineer-turned-coach would do, I built a platform I wished I had as a coach. That's how CoachSight was born.
            </p>
          </div>
        </div>

        {/* Right, founder card */}
        <div className="bg-dark-card rounded-2xl border border-dark-border shadow-sm p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-primary-600 rounded-2xl flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xl font-bold">PB</span>
            </div>
            <div>
              <p className="font-bold text-white">Pushan Bishi</p>
              <p className="text-sm text-gray-400">Founder, CoachSight</p>
              <p className="text-xs text-gray-500">20 years in tech · ADHD & Executive Function coach in training</p>
            </div>
          </div>
          <blockquote className="text-gray-300 text-sm leading-relaxed border-l-4 border-primary-700 pl-4 italic">
            "If CoachSight lets you be fully present with your client while quietly capturing the story of their journey, then I've built exactly what I set out to build."
          </blockquote>
          <div className="mt-6 p-4 bg-primary-900 rounded-xl border border-primary-800">
            <p className="text-xs text-primary-300 leading-relaxed">
              Engineer for 20+ years. Now building the tool I needed as a coach, one that thinks in Executive Function patterns, not just session transcripts.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Founding CTA ─────────────────────────────────────────────────────────────

function FoundingCta() {
  const perks = [
    { icon: <StarIcon className="w-5 h-5 text-primary-400" />, label: '30% lifetime discount' },
    { icon: <SparklesIcon className="w-5 h-5 text-primary-400" />, label: 'Direct influence on features' },
    { icon: <HeartIcon className="w-5 h-5 text-primary-400" />, label: 'Priority support' },
    { icon: <LockClosedIcon className="w-5 h-5 text-primary-400" />, label: 'Early access to new AI capabilities' },
  ];

  return (
    <section className="bg-primary-900 py-16">
      <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          <div className="w-14 h-14 bg-primary-600 bg-opacity-30 rounded-2xl flex items-center justify-center flex-shrink-0">
            <UsersIcon className="w-7 h-7 text-primary-300" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">Join the Founding Coach Program</h2>
            <p className="text-primary-300 text-sm">Help shape the future of ADHD coaching technology.</p>
          </div>
        </div>
        <div className="flex flex-col items-center gap-2 flex-shrink-0">
          <button
            onClick={openEarlyInterest}
            className="bg-primary-500 hover:bg-primary-400 text-white font-semibold px-8 py-3 rounded-xl transition-colors text-sm whitespace-nowrap"
          >
            Request Early Access
          </button>
          <p className="text-primary-400 text-xs">No credit card · No commitment</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {perks.map((p, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary-600 bg-opacity-30 rounded-lg flex items-center justify-center flex-shrink-0">
              {p.icon}
            </div>
            <span className="text-sm text-gray-300 font-medium">{p.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Pricing ──────────────────────────────────────────────────────────────────

function Pricing() {
  const plans = [
    { name: 'Starter', price: 24.99, founderPrice: 17.49, sessions: 'Up to 10 sessions/mo', storage: '1 GB storage' },
    { name: 'Growth',  price: 39.99, founderPrice: 27.99, sessions: 'Up to 30 sessions/mo', storage: '2 GB storage' },
    { name: 'Elite',   price: 69.99, founderPrice: 48.99, sessions: 'Unlimited sessions',    storage: '3 GB storage' },
  ];

  return (
    <section id="pricing" className="bg-dark-sidebar py-12 scroll-mt-16">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-extrabold text-white mb-3">Simple, transparent pricing</h2>
          <p className="text-gray-400 text-sm">14-day free trial. No credit card required.</p>
        </div>

        {/* Founding Member callout */}
        <div className="bg-dark-canvas border border-dark-border rounded-2xl px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <StarIcon className="w-4 h-4 text-yellow-400" />
              <span className="text-sm font-bold text-white">Founding Coach Offer</span>
            </div>
            <p className="text-gray-400 text-sm">
              Founding Coaches use CoachSight <span className="font-semibold text-white">free during beta</span>, then pay <span className="font-semibold text-white">30% less than listed price, forever</span>. Starter from <span className="font-semibold text-white">$17.49/mo</span>, Growth from <span className="font-semibold text-white">$27.99/mo</span>, Elite from <span className="font-semibold text-white">$48.99/mo</span>.
            </p>
          </div>
          <button
            onClick={openEarlyInterest}
            className="flex-shrink-0 bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-bold text-sm px-6 py-3 rounded-xl transition-colors whitespace-nowrap"
          >
            Claim Founding Rate
          </button>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className="bg-dark-card border border-dark-border rounded-2xl p-8 flex flex-col"
            >
              <h3 className="text-lg font-bold text-white mb-1">{plan.name}</h3>
              <div className="flex items-end gap-1 mb-4">
                <span className="text-4xl font-extrabold text-white">${plan.price.toFixed(2)}</span>
                <span className="text-sm text-gray-500 mb-1.5">/mo</span>
              </div>
              <div className="space-y-1.5 mb-6">
                <p className="text-sm font-semibold text-primary-400">{plan.sessions}</p>
                <p className="text-sm text-gray-400">{plan.storage}</p>
              </div>
              <button
                onClick={openEarlyInterest}
                className="mt-auto w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold text-sm py-3 rounded-xl transition-colors"
              >
                Start Free Trial
              </button>
            </div>
          ))}
        </div>

        {/* Session definition & over-plan policy */}
        <div className="text-center text-sm text-gray-400 mb-8 space-y-1 max-w-3xl mx-auto">
          <p>A session is any coaching session 15 minutes or more. Quick check-ins never count toward your plan, and neither do no-shows or cancellations.</p>
          <p>Go over your plan? Nothing happens, sessions are never cut off. Only two months in a row over your plan moves you up a tier.</p>
        </div>

        <p className="text-center text-sm text-gray-400 mb-4">Every feature is available on every plan. Plans differ only in monthly sessions and storage.</p>

        <p className="text-center text-xs text-gray-500 mt-4">
          All prices in USD. Cancel anytime. Introductory pricing, subject to change at launch; rates locked for Founding Coaches.
        </p>
      </div>
    </section>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────

type FaqEntry = { q: string; a: string };
type FaqGroup = { group: string; items: FaqEntry[] };

const faqGroups: FaqGroup[] = [
  {
    group: 'General',
    items: [
      {
        q: 'What is an ECR?',
        a: "ECR stands for Electronic Coaching Record, the coaching equivalent of an Electronic Medical Record (EMR) used in healthcare. Think of it like the medical record your doctor keeps, except instead of symptoms and prescriptions, it tracks your sessions, action items, progress, and the Executive Function patterns your coach notices over time. Everything in one place, building a real picture of your coaching journey session after session.",
      },
    ],
  },
  {
    group: 'Recording & AI',
    items: [
      {
        q: 'How does session recording work?',
        a: "CoachSight sends a notetaker bot to your Zoom or Google Meet session. It joins the call, records, and transcribes, you don't install anything or press record. After the session, your structured coaching notes, action items, and insights are ready for review, typically within minutes.",
      },
      {
        q: 'Does my client have to consent to recording?',
        a: "Yes, and it's enforced, not just a checkbox. AI processing only happens for clients who have explicitly consented. If a client hasn't consented, their sessions are never recorded or processed. The bot is also visible in the call, so there's no silent recording.",
      },
      {
        q: "Is my clients' data used to train AI models?",
        a: "No. Never. Your session content is not used to train or improve any AI model, ours or anyone else's. Additionally, client names are replaced with the generic term 'the client' before any transcript is processed.",
      },
      {
        q: 'What about my coaching style and intellectual property?',
        a: "They stay yours. CoachSight adapts to how you coach, you configure the note sections, their structure, and what gets captured to match your own methodology. Your frameworks, language, and approach are never used to train AI models, never shared with other coaches, and never absorbed into the product. The AI documents your coaching; it doesn't standardize it.",
      },
      {
        q: 'Can I edit what the AI produces?',
        a: "Everything. Notes, action items, and insights are drafts until you review them. Nothing reaches your client's record or their view without your approval. You can also configure the note sections themselves, add sections, remove them, change what goes in each, per client if you want.",
      },
      {
        q: 'What if the AI gets something wrong?',
        a: "That's exactly why nothing is auto-published. You review every note before it becomes part of the record. The AI does the heavy lifting; you stay the coach and the editor.",
      },
      {
        q: "Won't relying on AI limit my own insights and growth as a coach?",
        a: "CoachSight doesn't coach, it takes notes. The insight still happens where it always has: in the session, between you and your client. What changes is that you're no longer splitting attention between listening and documenting, and after the session you review a structured record instead of reconstructing one from memory. Many coaches find that reviewing their sessions this way sharpens their reflection rather than replacing it. The AI surfaces observations; deciding what they mean is, and stays, your craft.",
      },
    ],
  },
  {
    group: 'Working with clients',
    items: [
      {
        q: 'My practice is high-touch. Will my clients be interacting with AI?',
        a: "No. Your clients never talk to a bot, chat with an AI, or receive anything machine-generated on your behalf. Everything client-facing, session summaries, action items, nudges, carries your voice and reaches them only after you've reviewed and approved it. The AI works behind the scenes, for you alone. Your clients experience you, with more of your attention, not a layer of software between you and them.",
      },
      {
        q: 'What do my clients see?',
        a: "Clients get their own portal with their action items, session summaries (only the sections you choose to share), scheduled sessions, and nudges. You control section-by-section what's visible to them, your private coach notes are never shared.",
      },
      {
        q: 'How does CoachSight track client progress?',
        a: "Three ways, all built into the flow of your coaching. First, CoachSight generates trackers from what actually comes up in your sessions, short check-ins your clients answer periodically, charting how they're doing over time. Second, session notes are progress-aware: the AI looks back at previous sessions and what's happened since, and documents the progress it sees. Third, action item completion is reported right in your session notes, so follow-through is visible session over session. Progress stops being a feeling and becomes something you and your client can both point to.",
      },
      {
        q: 'What are nudges?',
        a: 'Scheduled reminders sent to your clients between sessions, by email, with your personal context attached. For ADHD and Executive Function clients, the space between sessions is where follow-through breaks down; nudges are how you stay present there without manually chasing anyone.',
      },
      {
        q: 'Can clients book sessions themselves?',
        a: 'Yes. You set your availability, connect Google Calendar, and clients pick a slot. The session lands on both calendars with the meeting link attached.',
      },
    ],
  },
  {
    group: 'Practical',
    items: [
      {
        q: 'What tools does CoachSight replace?',
        a: 'Typically: your note-taking app, a scheduling tool, a document-sharing setup, invoicing software, and a reminders system. Notes, scheduling, documents, payments, and client communication live in one place and are actually connected to each other.',
      },
      {
        q: 'Where is CoachSight available?',
        a: 'CoachSight is currently available to coaches practicing in the United States and Canada. SMS delivery is US/Canada only; email works everywhere. We plan to expand to more regions after launch.',
      },
      {
        q: 'What do I need to get started?',
        a: 'A Google account for calendar sync and the video platform you already use (Zoom or Google Meet). Setup is a matter of minutes, not days, connect your calendar, add a client, and your next session can be recorded.',
      },
      {
        q: 'Do I have to use the AI features?',
        a: "No. If a client hasn't consented, or you'd rather document a session yourself, you can log sessions manually with your own notes. Everything else, scheduling, action items, documents, invoicing, works the same.",
      },
    ],
  },
  {
    group: 'Pricing & commitment',
    items: [
      {
        q: 'How does the free trial work?',
        a: '14 days, full access, no credit card required. Cancel anytime, there are no contracts or setup fees.',
      },
      {
        q: 'What counts as a session?',
        a: "Any coaching session longer than 15 minutes, with or without AI. Quick check-ins never count toward your plan, and neither do no-shows or canceled sessions. Everything else on the platform, scheduling, action items, documents, nudges, is unlimited on every plan.",
      },
      {
        q: 'What happens if I go over my plan?',
        a: 'Nothing, immediately. Sessions are never cut off and there are no surprise charges, a busy month is just a busy month. Only if you exceed your plan two months in a row do we move you to the next tier, with advance notice and billed going forward only. And if your volume drops, you can downgrade anytime, yourself.',
      },
      {
        q: 'What is the Founding Coach Program?',
        a: "Early-access coaches use CoachSight free during beta, then pay 30% less than list price, locked in forever. Founding Coaches also get priority support and direct influence on what we build next. And there's no obligation: joining the beta doesn't commit you to a paid plan. If CoachSight isn't for you, walk away anytime, the Founding rate is simply there if you stay.",
      },
      {
        q: 'What happens to my data if I leave?',
        a: "It's yours. Your notes, client records, and documents belong to you, export everything at any time, and if you want it gone, delete your account and data permanently. Both coaches and clients have this right. We don't hold your practice hostage.",
      },
    ],
  },
];

function FaqItem({ item }: { item: FaqEntry }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-dark-card rounded-2xl border border-dark-border">
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 text-left px-5 py-4"
      >
        <span className="text-sm font-bold text-white">{item.q}</span>
        <ChevronDownIcon
          className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <p className="px-5 pb-4 text-sm text-gray-400 leading-relaxed">{item.a}</p>
      )}
    </div>
  );
}

function Faq() {
  return (
    <section id="faq" className="bg-dark-canvas py-12 scroll-mt-16">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-10">
          <p className="text-xs font-semibold uppercase tracking-widest text-primary-400 mb-2">FAQ</p>
          <h2 className="text-3xl font-extrabold text-white mb-3">Frequently asked questions</h2>
          <p className="text-gray-400 text-sm">
            Everything coaches ask us before getting started. Something else on your mind?{' '}
            <Link to="/support" className="text-primary-400 font-semibold hover:underline">Get in touch</Link>.
          </p>
        </div>
        <div className="space-y-8">
          {faqGroups.map((g) => (
            <div key={g.group}>
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-3">{g.group}</p>
              <div className="space-y-3">
                {g.items.map((item) => (
                  <FaqItem key={item.q} item={item} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="bg-dark-sidebar border-t border-dark-border py-8">
      <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-primary-600 rounded-lg flex items-center justify-center">
            <SparklesIcon className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-white text-sm">CoachSight</span>
          <span className="bg-primary-600 text-white text-xs font-bold px-1.5 py-0.5 rounded">ECR</span>
          <span className="text-gray-500 text-xs ml-2">The Electronic Coaching Record Platform.</span>
        </div>
        <div className="flex items-center gap-6 text-xs text-gray-500">
          <Link to="/privacy" onClick={() => window.scrollTo(0, 0)} className="hover:text-primary-400 transition-colors">Privacy</Link>
          <Link to="/terms" onClick={() => window.scrollTo(0, 0)} className="hover:text-primary-400 transition-colors">Terms</Link>
          <span>© 2025 CoachSight. All rights reserved.</span>
        </div>
      </div>
    </footer>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function MarketingLandingPage() {
  return (
    <div className="min-h-screen bg-dark-canvas font-sans">
      <Hero />

      <ThreeColSection />
      <WhyCoachSight />
      <AiTrust />
      <About />
      <Pricing />
      <Faq />
      <FoundingCta />
      <Footer />
    </div>
  );
}
